import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap, TimelineLite, TweenLite} from "gsap";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {TheCryptHelper} from "../utils/TheCryptHelper";
import {TheCryptGameAssets} from "../TheCryptGameAssets";
import {TheCrypt} from "../TheCrypt";
import {TheCryptWinConfig} from "../configs/TheCryptWinConfig";
import {BitmapText} from "pixi.js";
import Spine = PIXI.spine.Spine;
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {TheCryptSoundManager} from "../sound/TheCryptSoundManager";
import {TheCryptSoundConfig} from "../configs/TheCryptSoundConfig";

export class TheCryptBigWinView extends PIXI.Container implements StageLayer {
    private _countUp: BasicBitmapCountUp;
    private _spines: PIXI.spine.Spine[];
    private _ghost: PIXI.spine.Spine;
    private _waysNumber: BitmapText;
    private _waysText: PIXI.Sprite;
    private _maxLevel: number;

    constructor() {
        super();
        this.name = "BIG_WIN";
        this._spines = [];
        this.init();
    }

    public set maxLevel(value:number){
        this._maxLevel = value;
    }

    private init(): void {
        this._countUp = new BasicBitmapCountUp({
            fontName: TheCryptGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 54,
            tint: 0xffffff
        }, 660, new PIXI.Point(0.5, 0.5));
        this._countUp.position.set(360, 600);
        this._countUp.stopAt(0);

        this._ghost = new Spine(TheCrypt.assetLoader.getSpineAsset("ghost"));
        this._ghost.position.set(360, 360);
        this._ghost.visible = false;

        this.addChild(this._countUp);

        this._waysNumber = new BitmapText("", {
            fontName: TheCryptGameAssets.BITMAP_FONT_WIN_WAYS,
            fontSize: 40,
            tint: 0xffffff
        });
        this._waysNumber.letterSpacing = -12;
        this._waysText = new PIXI.Sprite();
        (<PIXI.Point>this._waysNumber.anchor).set(0, 1);
        this._waysText.anchor = new PIXI.Point(0, 0.7);

        this.visible = false;
        this.alpha = 0;
        this._countUp.visible = false;
    }

    public show(duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        this.alpha = 0;
        this.visible = true;
        this._countUp.scale.set(0.5);
        this._countUp.position.set(360, 600);
        tl.add(() => {
            TheCrypt.bigWinView._countUp.setAmount(0);
        });
        tl.add(gsap.to(this, {duration: duration, alpha: 1}), 0);
        const totalWinWays: number = TheCrypt.model.gameData.waysWonTotalRound;
        this._waysText.texture = TheCrypt.assetLoader.getTextures(`Way${totalWinWays > 1 ? "s" : ""}`)[0];
        this._waysNumber.text = totalWinWays.toString();
        const startPos: number = 360 - (this._waysNumber.width + this._waysText.width) * 0.5
        this._waysNumber.position.set(startPos, 560);
        this._waysText.position.set(startPos + this._waysNumber.width + 8, 560);
        this._waysNumber.alpha = 0;
        this._waysText.alpha = 0;
        this.addChild(this._waysText, this._waysNumber);

        return tl;
    }

    public hide(duration: number = 0.0001): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const maxLevel: number = TheCryptHelper.getBigWinLevel();
        tl.add(gsap.to(this._countUp, {duration: 0.2, y: 300}));
        tl.add(TheCrypt.gameViewContainer.removeDimmer(), 0.2);
        tl.add(gsap.to(this, {duration: duration, alpha: 0}));
        tl.add(() => {
            this.visible = false;
            this._countUp.visible = false;
            this._ghost.visible = false;
            this._spines.forEach((spine: Spine) => {
                spine.destroy();
            });
            this._spines = [];
        });

        return tl;
    }

    private getSpine(level: number): Spine {
        let spine: Spine = new PIXI.spine.Spine(TheCrypt.assetLoader.getSpineAsset("bigWinLv" + level.toString()));
        spine.position.set(360, 360);
        spine.name = "BIGWIN_SPINE_LEVEL_" + level;
        this._spines.push(spine);
        spine.visible = false;
        return spine;
    }

    public getLevelAnimation(countFrom: number, countTo: number, duration: number, level: number): gsap.core.Timeline {
        Logger.logDev("getLevelAnimation called details :: from : " + countFrom + " to : " + countTo + " duration : " + duration + " level : " + level);
        const tl: TimelineLite = new TimelineLite(),
            spine: PIXI.spine.Spine = this.getSpine(level),
            isLastLevel:boolean = (level === this._maxLevel);

        tl.add(() => {
            this.addChild(spine);
            this.addChild(this._countUp);
            this.addChild(this._ghost);
            if (level > 1) {
                this._spines[level - 2].visible = false;
            }
        });
        const scaleTl: TimelineLite = new TimelineLite(),
            delay: number = 0;
        scaleTl.add(this.getCountUpScaleAnimation(0.1, "power2.in", 0.4 + level * 0.1));
        scaleTl.add(this.getCountUpScaleAnimation(0.2, "power2.out", 0.5 + (level + 1) * 0.1));
        scaleTl.add(this.getCountUpScaleAnimation(duration - 0.3, "power2.in", 0.5 + (level + 1) * 0.1));
        tl.add(() => {
            this._ghost.visible = true;
            new SpineTween(this._ghost, 0, `lv${level}`);
            new SpineTween(spine, 0, "A");
            spine.visible = true;
        }, delay);
        tl.add(scaleTl, delay);
        if (level == 1) {
            tl.add(() => {
                this._countUp.visible = true;
            }, delay);
        }
        tl.add(this._countUp.getCountAnimation(countFrom, countTo, duration - 0.1, "none", level), delay + 0.1);
        if (isLastLevel) {
            tl.add([
                ()=>TheCryptSoundManager.playBigWinEndSound(level-1),
                TweenLite.to(this._waysNumber, 0.1, {alpha: 1}),
                TweenLite.to(this._waysText, 0.1, {alpha: 1})
            ]);
        }

        return tl;
    }

    public onAbort(level: number): void {
        TheCryptSoundManager.stopSoundEffect(TheCryptSoundConfig.BIGWIN[level]);
    }

    private getCountUpScaleAnimation(duration: number, ease: string, to: number, from?: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (from != undefined) {
            tl.add(() => {
                this._countUp.scale.set(from, from)
            });
        }
        tl.add(gsap.to(this._countUp.scale, {duration: duration, x: to, y: to, ease: ease}));
        return tl;
    }
}
